/**
 * ALLPOWERS 德国站｜V28 底栏 Klarna 官方 App Block
 *
 * 显示规则：
 * - 原产品加购/结账按钮不在可视区域时显示。
 * - 任一原购买按钮进入可视区域时自动隐藏。
 * - PC 与移动端可分别在 Section 后台开关。
 *
 * 购买规则：
 * - 加入购物车继续调用 Electro 主题原生产品表单。
 * - 立即购买优先调用 Shopify 原生动态结账按钮。
 * - 原生动态结账按钮不可用时，才使用 Cart API 加购后进入结账页。
 * - 价格区官方 Klarna Placement 始终保留。
 * - 底栏 Klarna 由独立的 Shopify 官方 App Block 输出。
 * - 不创建、复制或移动 Klarna Placement，避免与 Klarna App 冲突。
 * - 产品或套装切换时只同步金额并调用 App 官方更新接口。
 * - 优先使用 IntersectionObserver，不使用滚动轮询。
 */
(function () {
  "use strict";

  const ROOT_SELECTOR = "[data-de-bottom-buy-bar]";
  const MAIN_FORM_SELECTOR = ".js-main-product-form";
  const MAIN_BUTTONS_SELECTOR =
    ".js-main-product-form .product-form__buttons";
  const MAIN_ADD_BUTTON_SELECTOR =
    ".js-main-product-form .js-product-form-submit-btn";
  const DYNAMIC_CHECKOUT_SELECTOR =
    ".js-main-product-form .shopify-payment-button__button";
  const PREORDER_MANIFEST_SELECTOR = "[data-de-preorder-variants]";
  const ROOT_STATES = new WeakMap();

  function getPreorderManifest() {
    const element = document.querySelector(PREORDER_MANIFEST_SELECTOR);
    if (!element) return {};

    try {
      return JSON.parse(element.textContent || "{}");
    } catch (error) {
      return {};
    }
  }

  function isPreorderVariant(variantId) {
    const manifest = getPreorderManifest();
    return Boolean(
      variantId &&
        manifest[String(variantId)] &&
        manifest[String(variantId)].enabled === true
    );
  }

  function isMobileViewport() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  function isViewportEnabled(root) {
    if (isMobileViewport()) {
      return root.dataset.showMobile !== "false";
    }
    return root.dataset.showDesktop !== "false";
  }

  function setVisible(root, visible) {
    const nextVisible = Boolean(visible && isViewportEnabled(root));

    root.classList.toggle("is-visible", nextVisible);
    root.setAttribute("aria-hidden", nextVisible ? "false" : "true");

  }

  function getKlarnaPurchaseAmount(root) {
    const amount = Number(
      root.dataset.currentPrice || root.dataset.initialPrice || 0
    );

    return Number.isFinite(amount) && amount > 0 ? Math.round(amount) : 0;
  }

  function getSourceKlarnaPlacement(root) {
    const placements = Array.from(
      document.querySelectorAll("klarna-placement")
    ).filter(
      (placement) =>
        !root.contains(placement) &&
        placement.dataset.deBottomBarPlacement !== "true"
    );

    return (
      placements.find((placement) => placement.closest(".main-product")) ||
      placements.find((placement) =>
        placement.hasAttribute("data-purchase-amount")
      ) ||
      placements[0] ||
      null
    );
  }

  function requestKlarnaRefresh(amount) {
    if (
      window.KOSMApp &&
      typeof window.KOSMApp.updatePurchaseAmount === "function"
    ) {
      window.KOSMApp.updatePurchaseAmount(amount);
      return;
    }

    try {
      if (
        window.Klarna &&
        window.Klarna.OnsiteMessaging &&
        typeof window.Klarna.OnsiteMessaging.refresh === "function"
      ) {
        window.Klarna.OnsiteMessaging.refresh();
        return;
      }
    } catch (error) {
      // Klarna Web SDK 尚未完成初始化时，由下方兼容方式接管。
    }

    if (
      window.KlarnaOnsiteService &&
      typeof window.KlarnaOnsiteService.push === "function"
    ) {
      window.KlarnaOnsiteService.push({
        eventName: "refresh-placements"
      });
    }
  }

  function syncKlarnaPurchaseAmount(root, amount) {
    const normalizedAmount = Number(amount);
    if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) return;

    const roundedAmount = Math.round(normalizedAmount);
    root.dataset.currentPrice = String(roundedAmount);

    const bottomPlacement =
      root.querySelector("[data-de-buy-bar-klarna] klarna-placement");
    const sourcePlacement = getSourceKlarnaPlacement(root);

    [sourcePlacement, bottomPlacement].filter(Boolean).forEach((placement) => {
      placement.setAttribute(
        "data-purchase-amount",
        String(roundedAmount)
      );
    });
    requestKlarnaRefresh(roundedAmount);
  }

  function isElementVisibleInViewport(element) {
    if (!element) return false;

    const style = window.getComputedStyle(element);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      Number(style.opacity) === 0
    ) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight
    );
  }

  function syncButtonState(root) {
    const mainAddButton = document.querySelector(MAIN_ADD_BUTTON_SELECTOR);
    const stickyAddButton = root.querySelector(
      ".de-buy-bar__button--cart"
    );
    const stickyBuyButton = root.querySelector(
      "[data-de-sticky-buy-now]"
    );
    const stickyActions = root.querySelector(".de-buy-bar__actions");
    const currentVariantId = getCurrentVariantId(root);
    const isPreorder = isPreorderVariant(currentVariantId);
    const unavailable =
      !isPreorder &&
      (
        !mainAddButton ||
        mainAddButton.disabled ||
        mainAddButton.getAttribute("aria-disabled") === "true"
      );

    if (stickyAddButton) {
      stickyAddButton.disabled = unavailable;
      stickyAddButton.classList.toggle(
        "d-none-important",
        !isPreorder &&
          stickyActions &&
          stickyActions.dataset.purchaseType === "dynamic_checkout"
      );
      stickyAddButton.setAttribute(
        "aria-disabled",
        unavailable ? "true" : "false"
      );
    }

    if (stickyBuyButton) {
      stickyBuyButton.hidden = isPreorder;
      stickyBuyButton.classList.toggle("d-none-important", isPreorder);
      stickyBuyButton.setAttribute(
        "aria-hidden",
        isPreorder ? "true" : "false"
      );
      stickyBuyButton.disabled = isPreorder || unavailable;
      stickyBuyButton.setAttribute(
        "aria-disabled",
        isPreorder || unavailable ? "true" : "false"
      );
    }

    if (stickyActions) {
      stickyActions.classList.toggle(
        "de-buy-bar__actions--cart-only",
        isPreorder
      );
      stickyActions.classList.toggle(
        "de-buy-bar__actions--buy-only",
        !isPreorder &&
          stickyActions.dataset.purchaseType === "dynamic_checkout"
      );
    }

    if (stickyAddButton && mainAddButton) {
      const mainText = mainAddButton.querySelector(
        ".js-product-form-submit-btn-text"
      );
      const stickyText = stickyAddButton.querySelector(
        ".js-product-form-submit-btn-text"
      );

      if (mainText && stickyText && (isPreorder || unavailable)) {
        stickyText.textContent =
          isPreorder
            ? (mainText.textContent.trim() || "In den Warenkorb")
            : mainText.textContent.trim();
      }
    }
  }

  function syncSelectedProductMedia(root, variantId) {
    if (!variantId) return;

    const cards = Array.from(
      document.querySelectorAll("[data-de-option-card]")
    );
    const selectedCard = cards.find(
      (card) => String(card.dataset.variantId) === String(variantId)
    );
    const stickyImage = root.querySelector(".de-buy-bar__image");

    if (!selectedCard || !stickyImage || !selectedCard.dataset.imageSrc) {
      return;
    }

    stickyImage.src = selectedCard.dataset.imageSrc;
    stickyImage.srcset = "";
    stickyImage.alt = selectedCard.dataset.imageAlt || "";
  }

  function getSelectedOptionCard(variantId) {
    if (!variantId) return null;

    return Array.from(
      document.querySelectorAll("[data-de-option-card]")
    ).find(
      (card) => String(card.dataset.variantId) === String(variantId)
    );
  }

  function replaceFromTemplate(target, template) {
    if (!target || !template) return;
    target.replaceChildren(template.content.cloneNode(true));
  }

  function syncSelectedOption(root, variantId) {
    const selectedCard = getSelectedOptionCard(variantId);
    if (!selectedCard) return;

    const stickyTitle = root.querySelector(
      ".js-product-sticky-info-variant"
    );
    const stickyPrice = root.querySelector(
      ".js-product-sticky-info-price"
    );
    const priceTemplate = selectedCard.querySelector(
      "[data-de-option-price-template]"
    );

    if (stickyTitle) {
      stickyTitle.textContent = selectedCard.dataset.cardTitle || "";
    }

    replaceFromTemplate(stickyPrice, priceTemplate);
    syncSelectedProductMedia(root, variantId);
    syncKlarnaPurchaseAmount(root, selectedCard.dataset.variantPrice);

    root
      .querySelectorAll(
        ".js-product-sticky-form-input-id, [name='id']"
      )
      .forEach((input) => {
        input.disabled = false;
        input.value = String(variantId);
        input.setAttribute("value", String(variantId));
      });
  }

  function getCurrentVariantId(root) {
    const stickyInput = root.querySelector(
      ".js-product-sticky-form-input-id, [name='id']"
    );
    const mainInput = document.querySelector(
      `${MAIN_FORM_SELECTOR} [name="id"]`
    );

    return String(
      (stickyInput && stickyInput.value) ||
        (mainInput && mainInput.value) ||
        ""
    ).trim();
  }

  function getCurrentQuantity(root) {
    const mainQuantity = document.querySelector(
      `${MAIN_FORM_SELECTOR} [name="quantity"]`
    );
    const stickyQuantity = root.querySelector('[name="quantity"]');
    const value = Number(
      (mainQuantity && mainQuantity.value) ||
        (stickyQuantity && stickyQuantity.value) ||
        1
    );

    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
  }

  async function fallbackCheckout(root, button) {
    const mainForm = document.querySelector(MAIN_FORM_SELECTOR);
    const variantId = getCurrentVariantId(root);

    if (!variantId) {
      throw new Error("Keine gültige Produktvariante ausgewählt.");
    }

    const formData = mainForm ? new FormData(mainForm) : new FormData();
    if (!formData.get("id")) {
      formData.set("id", variantId);
    }
    if (!formData.get("quantity")) {
      formData.set("quantity", String(getCurrentQuantity(root)));
    }

    button.classList.add("is-loading");
    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    const rootUrl =
      window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root
        : "/";
    const normalizedRoot = rootUrl.endsWith("/") ? rootUrl : `${rootUrl}/`;

    const response = await fetch(`${normalizedRoot}cart/add.js`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: formData
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(
        payload.description ||
          payload.message ||
          "Der Artikel konnte nicht hinzugefügt werden."
      );
    }

    window.location.assign(`${normalizedRoot}checkout`);
  }

  function showCheckoutError(root, message) {
    const wrapper = root.querySelector(
      ".js-product-form-error-wrapper"
    );
    const output = root.querySelector(
      ".js-product-form-error-message"
    );

    if (wrapper && output) {
      output.textContent = message;
      wrapper.hidden = false;
    }
  }

  function handleBuyNow(root, button) {
    if (!button || button.disabled) return;

    const dynamicCheckoutButton = document.querySelector(
      DYNAMIC_CHECKOUT_SELECTOR
    );

    if (
      dynamicCheckoutButton &&
      !dynamicCheckoutButton.disabled &&
      dynamicCheckoutButton.getAttribute("aria-disabled") !== "true"
    ) {
      dynamicCheckoutButton.click();
      return;
    }

    fallbackCheckout(root, button).catch((error) => {
      button.classList.remove("is-loading");
      button.disabled = false;
      button.removeAttribute("aria-busy");
      showCheckoutError(
        root,
        error && error.message
          ? error.message
          : "Beim Starten des Checkouts ist ein Fehler aufgetreten."
      );
    });
  }

  function initializeBottomBar(root) {
    if (!root || root.dataset.deBottomBuyBarReady === "true") return;
    root.dataset.deBottomBuyBarReady = "true";

    const mainAddButton = document.querySelector(MAIN_ADD_BUTTON_SELECTOR);
    const dynamicCheckoutButton = document.querySelector(
      DYNAMIC_CHECKOUT_SELECTOR
    );
    const originalButtons = Array.from(
      new Set(
        [mainAddButton, dynamicCheckoutButton].filter(Boolean)
      )
    );
    const originalButtonsWrapper = document.querySelector(
      MAIN_BUTTONS_SELECTOR
    );
    const buyNowButton = root.querySelector("[data-de-sticky-buy-now]");
    const mainVariantInput = document.querySelector(
      `${MAIN_FORM_SELECTOR} input[name="id"]`
    );
    const state = {
      buttonObserver: null,
      intersectionObserver: null,
      optionChangeHandler: null,
      preorderChangeHandler: null,
      variantChangeHandler: null,
      buyNowHandler: null,
      requestUpdate: null,
      resizeObserver: null,
      scrollFallbackEnabled: false
    };
    let frameId = 0;

    ROOT_STATES.set(root, state);

    const updateVisibility = () => {
      frameId = 0;
      const originalPurchaseButtonVisible = originalButtons.some(
        isElementVisibleInViewport
      );
      setVisible(root, !originalPurchaseButtonVisible);
      syncButtonState(root);
    };

    const requestUpdate = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(updateVisibility);
    };
    state.requestUpdate = requestUpdate;

    if (!originalButtons.length) {
      setVisible(root, false);
      return;
    }

    window.addEventListener("resize", requestUpdate, { passive: true });
    window.addEventListener("orientationchange", requestUpdate);

    if ("IntersectionObserver" in window) {
      state.intersectionObserver = new IntersectionObserver(
        updateVisibility,
        { threshold: [0, 0.01] }
      );
      originalButtons.forEach((button) => {
        state.intersectionObserver.observe(button);
      });
    } else {
      state.scrollFallbackEnabled = true;
      window.addEventListener("scroll", requestUpdate, { passive: true });
    }

    if ("ResizeObserver" in window) {
      state.resizeObserver = new ResizeObserver(requestUpdate);
      originalButtons.forEach((button) => {
        state.resizeObserver.observe(button);
      });
      if (originalButtonsWrapper) {
        state.resizeObserver.observe(originalButtonsWrapper);
      }
      state.resizeObserver.observe(root);
    }

    if ("MutationObserver" in window && mainAddButton) {
      state.buttonObserver = new MutationObserver(() => {
        syncButtonState(root);
        requestUpdate();
      });
      state.buttonObserver.observe(mainAddButton, {
        attributes: true,
        attributeFilter: ["disabled", "aria-disabled", "class"],
        childList: true,
        subtree: true
      });
    }

    state.optionChangeHandler = (event) => {
      const variantId =
        event && event.detail ? event.detail.variantId : "";

      syncSelectedOption(root, variantId);
      syncButtonState(root);
      requestUpdate();
    };
    document.addEventListener(
      "de:product-option:change",
      state.optionChangeHandler
    );

    state.preorderChangeHandler = () => {
      syncButtonState(root);
      requestUpdate();
    };
    document.addEventListener(
      "de:preorder:change",
      state.preorderChangeHandler
    );

    if (mainVariantInput) {
      state.variantChangeHandler = () => {
        window.requestAnimationFrame(() => {
          syncButtonState(root);
          requestUpdate();
        });
      };
      mainVariantInput.addEventListener(
        "change",
        state.variantChangeHandler
      );
    }

    if (buyNowButton) {
      state.buyNowHandler = () => {
        handleBuyNow(root, buyNowButton);
      };
      buyNowButton.addEventListener("click", state.buyNowHandler);
    }

    const initiallySelectedCard = document.querySelector(
      "[data-de-option-card].is-selected"
    );

    if (initiallySelectedCard) {
      syncSelectedOption(root, initiallySelectedCard.dataset.variantId);
    }

    updateVisibility();
  }

  function destroyBottomBar(root) {
    const state = ROOT_STATES.get(root);
    if (!state) return;

    setVisible(root, false);
    if (state.buttonObserver) state.buttonObserver.disconnect();
    if (state.intersectionObserver) {
      state.intersectionObserver.disconnect();
    }
    if (state.resizeObserver) state.resizeObserver.disconnect();
    if (state.optionChangeHandler) {
      document.removeEventListener(
        "de:product-option:change",
        state.optionChangeHandler
      );
    }
    if (state.preorderChangeHandler) {
      document.removeEventListener(
        "de:preorder:change",
        state.preorderChangeHandler
      );
    }
    if (state.variantChangeHandler) {
      const mainVariantInput = document.querySelector(
        `${MAIN_FORM_SELECTOR} input[name="id"]`
      );
      if (mainVariantInput) {
        mainVariantInput.removeEventListener(
          "change",
          state.variantChangeHandler
        );
      }
    }
    if (state.buyNowHandler) {
      const buyNowButton = root.querySelector("[data-de-sticky-buy-now]");
      if (buyNowButton) {
        buyNowButton.removeEventListener("click", state.buyNowHandler);
      }
    }
    if (state.requestUpdate) {
      window.removeEventListener("resize", state.requestUpdate);
      window.removeEventListener("orientationchange", state.requestUpdate);
      if (state.scrollFallbackEnabled) {
        window.removeEventListener("scroll", state.requestUpdate);
      }
    }

    delete root.dataset.deBottomBuyBarReady;
    ROOT_STATES.delete(root);
  }

  function initializeAll(scope) {
    const context = scope && scope.querySelectorAll ? scope : document;
    context.querySelectorAll(ROOT_SELECTOR).forEach(initializeBottomBar);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initializeAll(document);
    });
  } else {
    initializeAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAll(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(destroyBottomBar);
  });
})();
